import { cn } from "@omni/utils/styles";
import { ReactNode } from "react";

import { CloseButton } from "@/components/General/Modal/components/closeButton";
import { ConfirmButton } from "@/components/General/Modal/components/confirmButton";
import { Dialog } from "@/components/General/Modal/components/dialog/dialog.lazy";
import { useModalContext } from "@/components/General/Modal/context/modal.context";
import { useModalRouter } from "@/utils/hooks/useModalRouter";
import { getTagIdFor } from "@/utils/tagging/tag.utils";

type Props = {
    className?: string;
    children?: ReactNode;
};

export const ModalDialog = ({ className, children }: Props) => {
    const {
        modalType,
        descriminator,
        title,
        subtitle,
        size,
        open,
        optimisticOpen,
        onClose,
        onCloseEnd,
        withFooter,
        autoFocus,
        closable,
        centered,
        scrollable,
        active
    } = useModalContext();

    const { previous } = useModalRouter(modalType);

    return (
        <Dialog
            lockId={modalType}
            className={className}
            open={open}
            optimisticOpen={optimisticOpen}
            onClose={onClose}
            onCloseEnd={onCloseEnd}
            centered={centered}
            size={size}
            autoFocus={autoFocus}
            scrollable={scrollable}
            active={active}
            closable={closable}
            {...getTagIdFor("modal", modalType, descriminator)}
        >
            <Dialog.Header
                tag={typeof title === "string" ? "h2" : "div"}
                handleOnClose={onClose}
                closable={closable}
                className={cn("tw:mb-4 tw:mt-0 tw:mx-0 tw:px-6")}
                showPrevious={!!previous}
            >
                {title}
                {subtitle && <div className={cn("tw:text-xl")}>{subtitle}</div>}
            </Dialog.Header>
            <Dialog.Body className={cn("tw:p-0")}>
                <div className={cn("tw:px-6")}>{children}</div>
            </Dialog.Body>
            {withFooter && (
                <Dialog.Footer
                    className={cn("tw:px-6 tw:flex tw:w-full tw:items-center", {
                        "tw:justify-between": closable,
                        "tw:justify-end": !closable
                    })}
                >
                    <CloseButton />
                    <ConfirmButton />
                </Dialog.Footer>
            )}
        </Dialog>
    );
};
